import React, { useEffect, useMemo } from 'react'

import { useTranslation } from 'react-i18next'

// MUI Imports
import Typography from '@mui/material/Typography'
import TablePagination from '@mui/material/TablePagination'
import Checkbox from '@mui/material/Checkbox'
import { Tooltip, Link } from '@mui/material'
import { ExpandMore, ExpandLess } from '@mui/icons-material'

// Third-party Imports
import classnames from 'classnames'
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
  getFilteredRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFacetedMinMaxValues,
  getPaginationRowModel,
  getSortedRowModel
} from '@tanstack/react-table'

// Component Imports
import { TablePaginationComponentSemiClientSide } from '../molecules/TablePaginationComponent'
import LoadingOverlay from '../atoms/LoadingOverlay'

// Utils Imports
import { getSuitabilityLevelInfo } from '../../../utils/helpers'

// Style Imports
import tableStyles from '../../styles/table.module.scss'

// Column Definitions
const columnHelper = createColumnHelper()

const getFirstWebsite = (web) => {
  if (!web) return null

  const websites = web.split(/[,;]/).map(w => w.trim()).filter(w => w)

  if (websites.length === 0) return null
  const firstWebsite = websites[0]

  return firstWebsite.indexOf('://') === -1 ? `http://${firstWebsite}` : firstWebsite
}

const CampaignPreparationTable = (props) => {
  const {
    data,
    rowSelection,
    setRowSelection,
    fetching,
    fuzzyFilter,
    globalFilter,
    setGlobalFilter,
    pageSize,
    currentPage = 0,
    totalElements = 0,
    totalPageCount = 0,
    onPageChange,
    disableInteraction = false,
    hideCheckboxes = false,
    sorting = [],
    onSortingChange
  } = props
  const { t } = useTranslation()

  const hasExplanation = useMemo(() => {
    return data.length > 0 && data.some(item => 'explanation' in item)
  }, [data])

  const columns = useMemo(
    () => {
      const columns = []

      if (!hideCheckboxes) {
        columns.push({
          id: 'select',
          header: ({ table }) => (
            <Checkbox
              {...{
                checked: table.getIsAllRowsSelected(),
                indeterminate: table.getIsSomeRowsSelected(),
                onChange: table.getToggleAllRowsSelectedHandler(),
                disabled: disableInteraction
              }}
            />
          ),
          cell: ({ row }) => (
            <Checkbox
              {...{
                checked: row.getIsSelected(),
                disabled: !row.getCanSelect() || disableInteraction,
                indeterminate: row.getIsSomeSelected(),
                onChange: row.getToggleSelectedHandler()
              }}
            />
          )
        })
      }

      columns.push(columnHelper.accessor('name', {
        header: t('mailTemplate.wizard.firmList.firmName'),
        meta: { flexGrow: 1 },
        cell: ({ row }) => {
          const firmName = row.original.name
          const website = getFirstWebsite(row.original.web)

          const nameContent = (
              <span style={{
                display: 'block',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                width: '100%',
                maxWidth: '300px'
              }}>
                {firmName}
              </span>
          )

          return (
              <div className="flex items-center gap-4" style={{ width: '100%', minWidth: '120px' }}>
                <div className="flex flex-col" style={{ width: '100%' }}>
                  <Typography color="text.primary" className="font-medium">
                    <Tooltip
                      title={firmName}
                      arrow
                      placement="top"
                      disableHoverListener={firmName.length <= 40}
                    >
                      {website
                        ? (
                          <Link
                            href={website}
                            target="_blank"
                            rel="noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            underline="hover"
                            sx={{
                              color: 'inherit !important',
                              '&:hover': { color: 'inherit !important' },
                              '&:visited': { color: 'inherit !important' },
                              '&:active': { color: 'inherit !important' }
                            }}
                          >
                            {nameContent}
                          </Link>
                          )
                        : (
                            nameContent
                          )}
                    </Tooltip>
                  </Typography>
                </div>
              </div>
          )
        }
      }))

      columns.push(columnHelper.accessor('suitabilityLevel', {
        header: t('mailTemplate.wizard.firmList.relevant'),
        sortingFn: (rowA, rowB) => {
          const levelOrder = { HIGHLY_SUITABLE: 1, MODERATELY_SUITABLE: 2, MINIMALLY_SUITABLE: 3, NOT_SUITABLE: 4, CANNOT_ACCESS: 5 }
          const levelA = levelOrder[rowA.original.suitabilityLevel] || 999
          const levelB = levelOrder[rowB.original.suitabilityLevel] || 999
          return levelA - levelB
        },
        cell: ({ row }) => {
          const suitabilityInfo = getSuitabilityLevelInfo(row.original.suitabilityLevel)

          return (
              <div className="flex items-center gap-4">
                <div className="flex flex-col gap-1" style={{ alignItems: 'center' }}>
                  {suitabilityInfo
                    ? (
                      <Tooltip
                        title={t(suitabilityInfo.labelKey)}
                        arrow
                        placement="top"
                      >
                        <div
                          style={{
                            backgroundColor: suitabilityInfo.color,
                            color: 'white',
                            width: '24px',
                            height: '24px',
                            borderRadius: '4px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '14px',
                            fontWeight: 'bold'
                          }}
                        >
                          {suitabilityInfo.level}
                        </div>
                      </Tooltip>
                      )
                    : (
                      <Typography variant="body2" style={{ color: 'rgba(158, 158, 158, 1)' }}>—</Typography>
                      )}
                </div>
              </div>
          )
        }
      }))

      if (hasExplanation) {
        columns.push(
          columnHelper.accessor('explanation', {
            header: t('mailTemplate.wizard.firmList.explanation'),
            meta: { flexGrow: 2 },
            cell: ({ row }) => {
              const explanation = row.original.explanation

              const decodedExplanation = explanation
                ? explanation.replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&quot;/g, '"').replace(/&#39;/g, '\'').replace(/&nbsp;/g, ' ')
                : explanation
              return (
                <div className="flex items-center gap-4">
                  <div className="flex flex-col" style={{ width: '100%', minWidth: '200px' }}>
                    {decodedExplanation
                      ? (
                        <Typography color="text.primary" className="font-medium">
                          <Tooltip
                            title={
                              <div style={{
                                display: '-webkit-box',
                                WebkitLineClamp: 10,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'pre-wrap'
                              }}>
                                {decodedExplanation}
                              </div>
                            }
                            arrow
                            placement="top"
                            disableHoverListener={decodedExplanation.length <= 80}
                          >
                          <span style={{
                            display: 'block',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            width: '100%',
                            maxWidth: '470px'
                          }}>
                            {decodedExplanation}
                          </span>
                          </Tooltip>
                        </Typography>
                        )
                      : (
                        <Typography variant="body2" style={{ color: 'rgba(158, 158, 158, 1)' }}>—</Typography>
                        )}
                  </div>
                </div>
              )
            }
          })
        )
      }

      return columns
    },
    [t, hasExplanation, disableInteraction, hideCheckboxes]
  )

  const table = useReactTable({
    data,
    columns,
    filterFns: {
      fuzzy: fuzzyFilter
    },
    state: {
      rowSelection,
      globalFilter,
      sorting,
      pagination: {
        pageIndex: currentPage,
        pageSize
      }
    },
    enableRowSelection: !disableInteraction,
    globalFilterFn: fuzzyFilter,
    onRowSelectionChange: setRowSelection,
    getCoreRowModel: getCoreRowModel(),
    onGlobalFilterChange: setGlobalFilter,
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    getFacetedMinMaxValues: getFacetedMinMaxValues(),
    manualSorting: true,
    onSortingChange: (updater) => {
      const newSorting = typeof updater === 'function' ? updater(sorting) : updater
      onSortingChange(newSorting)
    },
    manualPagination: true,
    pageCount: totalPageCount,
    totalRecordCount: totalElements,
    onPaginationChange: (updater) => {
      if (typeof updater === 'function') {
        const newState = updater({ pageIndex: currentPage, pageSize })
        if (newState.pageIndex !== currentPage && onPageChange) {
          onPageChange(newState.pageIndex)
        }
      }
    },
    autoResetPageIndex: false,
    getRowId: (row) => row.id
  })

  useEffect(() => {
    table.setPageSize(pageSize)
  }, [pageSize, table])

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '400px', height: '100%' }}>
      <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', position: 'relative' }}>
        {fetching && <LoadingOverlay />}
        <div className="overflow-x-auto" style={{ flex: 1 }}>
          <table className={tableStyles.table}>
            <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th key={header.id}>
                    {header.isPlaceholder
                      ? null
                      : (
                        <div
                          className={classnames({
                            'flex items-center': header.column.getIsSorted(),
                            'cursor-pointer select-none':
                              header.column.getCanSort()
                          })}
                          onClick={header.column.getToggleSortingHandler()}
                        >
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                          {{
                            asc: <ExpandLess/>,
                            desc: <ExpandMore/>
                          }[header.column.getIsSorted()] ?? null}
                        </div>
                        )}
                  </th>
                ))}
              </tr>
            ))}
            </thead>
            <tbody>
            {table.getFilteredRowModel().rows.length === 0
              ? (
                <tr>
                  <td
                    colSpan={table.getVisibleFlatColumns().length}
                    className="text-center"
                  >
                    {!fetching && t('mailTemplate.wizard.firmList.noData')}
                  </td>
                </tr>
                )
              : (
                  table.getRowModel().rows.slice(0, table.getState().pagination.pageSize).map((row) => {
                    return (
                    <tr
                      key={row.id}
                      className={classnames({ selected: row.getIsSelected() })}
                      style={{
                        cursor: disableInteraction ? 'default' : 'pointer'
                      }}
                      onClick={disableInteraction ? undefined : row.getToggleSelectedHandler()}
                    >
                      {row.getVisibleCells().map((cell) => (
                        <td key={cell.id}>
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </td>
                      ))}
                    </tr>
                    )
                  })
                )}
            </tbody>
          </table>
        </div>

        <div style={{ flexShrink: 0, marginTop: 'auto' }}>
          <TablePagination
            component={() => (
              <TablePaginationComponentSemiClientSide
                table={table}
                infoText1={t('mailTemplate.wizard.firmList.tableInfo_1')}
                infoText2={t('mailTemplate.wizard.firmList.tableInfo_2')}
              />
            )}
            count={totalElements}
            rowsPerPage={table.getState().pagination.pageSize}
            page={table.getState().pagination.pageIndex}
            onPageChange={(_, page) => {
              table.setPageIndex(page)
            }}
          />
        </div>
      </div>
    </div>
  )
}

export default CampaignPreparationTable
